import type { QueryClient } from "@tanstack/react-query";

/**
 * Maps a mutating MCP tool to query-key URL fragments to invalidate when it
 * completes, so the UI refreshes without a reload. Fragments are matched as
 * substrings against query keys; the leading slash avoids matching e.g.
 * "subprojects". Read-only tools are omitted; an unmapped tool just won't
 * auto-refresh until the user navigates.
 */
const TOOL_INVALIDATIONS: Partial<Record<string, readonly string[]>> = {
  "create-project": ["/projects"],
  "create-branch": ["/branches", "/projects"],
  "create-table": ["/tables", "/projects"],
  "create-material": ["/materials", "/gemd"],
  "create-default-predictor": ["/predictors", "/projects"],
  "create-default-design-space": ["/design-spaces", "/predictors"],
  "create-design-workflow": ["/design-workflows", "/branches"],
  "train-predictor": ["/predictors"],
  "trigger-design-execution": ["/executions", "/design-workflows"],
  "update-design-space": ["/design-spaces"],
  "update-design-workflow": ["/design-workflows"],
  "pin-candidates": ["/candidates", "/executions"],
  "remove-predictor-outputs": ["/predictors"],
  "add-expression-node": ["/predictors"],
};

// Matches "✓ <tool-name> complete" emitted by the agent runtime.
const TOOL_COMPLETE_REGEX = /^✓\s+([\w-]+)\s+complete/;

/** Returns the tool name if the event signals tool-call completion. */
export function parseToolCompleteEvent(eventData: string): string | undefined {
  return TOOL_COMPLETE_REGEX.exec(eventData)?.[1];
}

/** Invalidate caches affected by a completed tool call; no-op if unmapped. */
export function invalidateForTool(
  queryClient: QueryClient,
  toolName: string,
): void {
  const fragments = TOOL_INVALIDATIONS[toolName];
  if (!fragments) {
    return;
  }

  queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      Array.isArray(queryKey) &&
      queryKey.some(
        (segment) =>
          typeof segment === "string" &&
          fragments.some((fragment) => segment.includes(fragment)),
      ),
  });
}
